import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { apiGet, apiPatch } from '../../lib/api'
import { MarkPaidInput } from '@buddys-choice/shared'
import type { Invoice, MarkPaidInput as MarkPaidInputType } from '@buddys-choice/shared'

export const AdminInvoiceMarkPaidPage: React.FC = () => {
  const { invoiceId } = useParams({ from: '/admin/invoices/$invoiceId/mark-paid' })
  const navigate = useNavigate()
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitError, setSubmitError] = useState('')

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MarkPaidInputType>({
    resolver: zodResolver(MarkPaidInput),
    defaultValues: { paymentReference: '' },
  })

  useEffect(() => {
    apiGet<Invoice[]>('invoices')
      .then((rows) => {
        const found = rows.find((i) => i.id === invoiceId)
        if (!found) setLoadError('Invoice not found.')
        else setInvoice(found)
      })
      .catch((err: Error) => setLoadError(err.message || 'Failed to load invoice.'))
      .finally(() => setLoading(false))
  }, [invoiceId])

  const handleSubmit = async (data: MarkPaidInputType) => {
    setSubmitError('')
    try {
      await apiPatch(`invoices/${invoiceId}/mark-paid`, { paymentReference: data.paymentReference.trim() })
      navigate({ to: '/admin/invoices' })
    } catch (err: unknown) {
      setSubmitError((err as Error).message || 'Failed to mark invoice paid.')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <span className="spinner text-[var(--gold-dark)]" />
      </div>
    )
  }

  if (loadError || !invoice) {
    return (
      <div className="space-y-4">
        <Link to="/admin/invoices" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)]">
          <ArrowLeft className="w-4 h-4" /> Back to invoices
        </Link>
        <div className="alert alert-error text-sm">{loadError || 'Invoice not found.'}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-md">
      <Link to="/admin/invoices" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)]">
        <ArrowLeft className="w-4 h-4" /> Back to invoices
      </Link>

      <div className="glass-card p-6">
        <h1 className="text-xl font-serif font-bold mb-1">Mark invoice paid</h1>
        <p className="text-xs text-[var(--gold-dark)] font-mono mb-6">{invoice.invoiceNumber}</p>

        {submitError && <div className="alert alert-error text-xs mb-4">{submitError}</div>}

        <form onSubmit={handleFormSubmit(handleSubmit)} className="space-y-4 text-sm">
          <div className="form-group">
            <label className="form-label">Payment UTR / reference code *</label>
            <input
              type="text"
              {...register('paymentReference')}
              placeholder="e.g. UTR-30918203912 or CASH-RECEIPT"
              className="form-input font-mono"
              aria-invalid={!!errors.paymentReference}
            />
            {errors.paymentReference && <span className="form-error">{errors.paymentReference.message}</span>}
          </div>

          <div className="flex gap-3 pt-2">
            <Link to="/admin/invoices" className="btn btn-secondary flex-1 text-center">
              Cancel
            </Link>
            <button type="submit" disabled={isSubmitting} className="btn btn-primary flex-1 font-semibold">
              {isSubmitting ? <span className="spinner" /> : 'Confirm payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
